import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormHelperText,
  Divider,
  Fade,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Form validation functions
const validateField = (name, value) => {
  let error = '';
  
  switch(name) {
    case 'fullName':
      if (!value.trim()) {
        error = 'Full name is required';
      } else if (value.trim().length < 3) {
        error = 'Name must be at least 3 characters long';
      }
      break;
      
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) {
        error = 'Email is required';
      } else if (!emailRegex.test(value)) {
        error = 'Please enter a valid email address';
      }
      break;
      
    case 'phone':
      // Phone is optional but validate format if provided
      if (value.trim()) {
        const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
        if (!phoneRegex.test(value)) {
          error = 'Please enter a valid phone number';
        }
      }
      break;
      
    case 'company':
      if (!value.trim()) {
        error = 'Company name is required';
      }
      break;
      
    case 'serviceType':
      if (!value || value === '') {
        error = 'Please select a service type';
      }
      break;
      
    case 'budget':
      if (!value || value === '') {
        error = 'Please select a budget range';
      }
      break;
      
    case 'projectDescription':
      if (!value.trim()) {
        error = 'Project description is required';
      } else if (value.trim().length < 20) {
        error = 'Description should be at least 20 characters long';
      }
      break;
      
    case 'timeline':
      if (!value || value === '') {
        error = 'Please select your expected timeline';
      }
      break;
      
    default:
      break;
  }
  
  return error;
};

// Service options
const serviceOptions = [
  { value: 'web_development', label: 'Web Development' },
  { value: 'mobile_app', label: 'Mobile App Development' },
  { value: 'cloud_solutions', label: 'Cloud Solutions' },
  { value: 'ai_data', label: 'AI & Data Science' },
  { value: 'ui_ux', label: 'UI/UX Design' },
  { value: 'qa_testing', label: 'Quality Assurance' },
  { value: 'consulting', label: 'IT Consulting' },
  { value: 'other', label: 'Other' }
];

// Budget range options
const budgetOptions = [
  { value: 'under_5k', label: 'Under $5,000' },
  { value: '5k_15k', label: '$5,000 - $15,000' },
  { value: '15k_30k', label: '$15,000 - $30,000' },
  { value: '30k_50k', label: '$30,000 - $50,000' },
  { value: '50k_100k', label: '$50,000 - $100,000' },
  { value: 'above_100k', label: 'Above $100,000' },
  { value: 'not_sure', label: 'Not sure yet' }
];

// Timeline options
const timelineOptions = [
  { value: 'urgent', label: 'Urgent (ASAP)' },
  { value: '1_month', label: 'Within 1 month' },
  { value: '1_3_months', label: '1-3 months' },
  { value: '3_6_months', label: '3-6 months' },
  { value: 'flexible', label: 'Flexible' }
];

// Communication preference options
const communicationOptions = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'video_call', label: 'Video Call' },
  { value: 'in_person', label: 'In-Person Meeting (if available)' }
];

const ServiceRequestForm = ({ mode = 'light' }) => {
  // Multi-step form states
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  
  // Form data state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    otherService: '',
    budget: '',
    projectDescription: '',
    timeline: '',
    communicationPreference: ['email'],
    additionalInfo: '',
    attachments: null,
    howDidYouHear: ''
  });
  
  // Form validation state
  const [errors, setErrors] = useState({});
  
  // Field touched state to only show validation after user interaction
  const [touched, setTouched] = useState({});
  
  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Step titles for the stepper
  const steps = ['Contact Information', 'Project Details', 'Additional Information', 'Review & Submit'];
  
  // Color theme based on mode
  const theme = {
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#6a1bff' : '#5e00ff'
      },
      secondary: {
        main: mode === 'dark' ? '#00e5ff' : '#00b4cc'
      },
      background: {
        default: mode === 'dark' ? '#121212' : '#f5f7ff',
        paper: mode === 'dark' ? '#1a1a1a' : '#ffffff'
      },
      text: {
        primary: mode === 'dark' ? '#ffffff' : '#000000',
        secondary: mode === 'dark' ? '#b0b0b0' : '#555555'
      }
    }
  };
  
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };
  
  // Handle field change with validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validate field
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };
  
  // Handle checkbox group change
  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    
    setFormData(prev => {
      const preferences = prev[name] || [];
      if (checked) {
        return { ...prev, [name]: [...preferences, value] };
      } else {
        return { ...prev, [name]: preferences.filter(pref => pref !== value) };
      }
    });
  };
  
  // Handle file upload
  const handleFileChange = (e) => {
    const files = e.target.files;
    setFormData(prev => ({
      ...prev,
      attachments: files
    }));
  };
  
  // Handle field blur event to mark as touched
  const handleBlur = (e) => {
    const { name } = e.target;
    
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validate field on blur
    const error = validateField(name, formData[name]);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };
  
  // Validate the current step
  const validateStep = (step) => {
    let isValid = true;
    const newErrors = { ...errors };
    
    // Fields to validate for each step
    const fieldsByStep = {
      0: ['fullName', 'email', 'phone', 'company'],
      1: ['serviceType', 'budget', 'projectDescription', 'timeline'],
      2: ['communicationPreference'],
      3: [] // Nothing to validate in the review step
    };
    
    // Validate required fields for the current step
    fieldsByStep[step].forEach(field => {
      // Special handling for communication preference
      if (field === 'communicationPreference') {
        if (!formData[field] || formData[field].length === 0) {
          newErrors[field] = 'Please select at least one communication preference';
          isValid = false;
        } else {
          newErrors[field] = '';
        }
        return;
      }
      
      // For other fields
      const error = validateField(field, formData[field]);
      newErrors[field] = error;
      if (error) {
        isValid = false;
      }
      
      // Special case for 'other' service type
      if (field === 'serviceType' && formData[field] === 'other') {
        if (!formData.otherService.trim()) {
          newErrors.otherService = 'Please specify the service you need';
          isValid = false;
        } else {
          newErrors.otherService = '';
        }
      }
    });
    
    setErrors(newErrors);
    
    // Mark all fields in this step as touched
    const touchedFields = {};
    fieldsByStep[step].forEach(field => {
      touchedFields[field] = true;
      if (field === 'serviceType' && formData[field] === 'other') {
        touchedFields.otherService = true;
      }
    });
    
    setTouched(prev => ({
      ...prev,
      ...touchedFields
    }));
    
    return isValid;
  };
  
  // Handle next step button click
  const handleNext = () => {
    if (validateStep(activeStep)) {
      // Mark current step as completed
      setCompleted(prev => ({
        ...prev,
        [activeStep]: true
      }));
      
      // Move to next step
      setActiveStep(prevStep => prevStep + 1);
    }
  };
  
  // Handle back button click
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation of all steps
    let isValid = true;
    for (let i = 0; i < steps.length - 1; i++) {
      if (!validateStep(i)) {
        isValid = false;
        setActiveStep(i);
        break;
      }
    }
    
    if (isValid) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Simulate API call with timeout
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Success! In a real app, you would send the data to your backend
        console.log('Form submitted successfully:', formData);
        
        // Reset form state
        setIsSubmitting(false);
        setSubmitted(true);
      } catch (error) {
        console.error('Error submitting form:', error);
        setIsSubmitting(false);
        setSubmitError('There was an error submitting your request. Please try again or contact us directly.');
      }
    }
  };
  
  // Reset the form to start over
  const handleReset = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      serviceType: '',
      otherService: '',
      budget: '',
      projectDescription: '',
      timeline: '',
      communicationPreference: ['email'],
      additionalInfo: '',
      attachments: null,
      howDidYouHear: ''
    });
    setErrors({});
    setTouched({});
    setActiveStep(0);
    setCompleted({});
    setSubmitted(false);
    setSubmitError(null);
  };
  
  // Render form content based on the current step
  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Contact Information
        return (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Please provide your contact details so we can get in touch with you about your project.
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  variant="outlined"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.fullName && !!errors.fullName}
                  helperText={touched.fullName && errors.fullName}
                  required
                  InputProps={{
                    endAdornment: touched.fullName && (
                      <InputAdornment position="end">
                        {errors.fullName ? (
                          <ErrorIcon color="error" />
                        ) : (
                          <CheckCircleIcon color="success" />
                        )}
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  required
                  InputProps={{
                    endAdornment: touched.email && (
                      <InputAdornment position="end">
                        {errors.email ? (
                          <ErrorIcon color="error" />
                        ) : (
                          <CheckCircleIcon color="success" />
                        )}
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  variant="outlined"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.phone && !!errors.phone}
                  helperText={touched.phone && errors.phone}
                  InputProps={{
                    endAdornment: touched.phone && formData.phone && (
                      <InputAdornment position="end">
                        {errors.phone ? (
                          <ErrorIcon color="error" />
                        ) : (
                          <CheckCircleIcon color="success" />
                        )}
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company/Organization"
                  variant="outlined"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.company && !!errors.company}
                  helperText={touched.company && errors.company}
                  required
                  InputProps={{
                    endAdornment: touched.company && (
                      <InputAdornment position="end">
                        {errors.company ? (
                          <ErrorIcon color="error" />
                        ) : (
                          <CheckCircleIcon color="success" />
                        )}
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </motion.div>
        );
        
      case 1: // Project Details
        return (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Project Details
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Tell us about your project requirements and expectations.
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth
                  error={touched.serviceType && !!errors.serviceType}
                  required
                >
                  <InputLabel id="service-type-label">Service Type</InputLabel>
                  <Select
                    labelId="service-type-label"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Service Type"
                  >
                    {serviceOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.serviceType && errors.serviceType && (
                    <FormHelperText>{errors.serviceType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              {formData.serviceType === 'other' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Specify Service"
                    variant="outlined"
                    name="otherService"
                    value={formData.otherService}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.otherService && !!errors.otherService}
                    helperText={touched.otherService && errors.otherService}
                    required
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth
                  error={touched.budget && !!errors.budget}
                  required
                >
                  <InputLabel id="budget-label">Budget Range</InputLabel>
                  <Select
                    labelId="budget-label"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Budget Range"
                  >
                    {budgetOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.budget && errors.budget && (
                    <FormHelperText>{errors.budget}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Description"
                  variant="outlined"
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.projectDescription && !!errors.projectDescription}
                  helperText={touched.projectDescription && errors.projectDescription}
                  required
                  multiline
                  rows={4}
                  placeholder="Please describe your project requirements, goals, and any specific features you need..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth
                  error={touched.timeline && !!errors.timeline}
                  required
                >
                  <InputLabel id="timeline-label">Expected Timeline</InputLabel>
                  <Select
                    labelId="timeline-label"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Expected Timeline"
                  >
                    {timelineOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.timeline && errors.timeline && (
                    <FormHelperText>{errors.timeline}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </motion.div>
        );
        
      case 2: // Additional Information
        return (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Help us serve you better with these additional details.
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Preferred Communication Method
                </Typography>
                <FormGroup>
                  <Grid container spacing={1}>
                    {communicationOptions.map(option => (
                      <Grid item xs={6} key={option.value}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.communicationPreference.includes(option.value)}
                              onChange={handleCheckboxChange}
                              name="communicationPreference"
                              value={option.value}
                              color="primary"
                            />
                          }
                          label={option.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
                {touched.communicationPreference && errors.communicationPreference && (
                  <FormHelperText error>{errors.communicationPreference}</FormHelperText>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Information (Optional)"
                  variant="outlined"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Any other details you'd like to share about your project..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="How did you hear about us? (Optional)"
                  variant="outlined"
                  name="howDidYouHear"
                  value={formData.howDidYouHear}
                  onChange={handleChange}
                  placeholder="Google, Social Media, Referral, etc."
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Attach Files (Optional)
                </Typography>
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'rgba(106, 27, 255, 0.05)',
                  }}
                >
                  <input
                    type="file"
                    multiple
                    id="file-upload"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      sx={{ mb: 1 }}
                    >
                      Select Files
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Upload project requirements, mockups, or any relevant documents (Max: 5MB each)
                  </Typography>
                  
                  {formData.attachments && (
                    <Box sx={{ mt: 2, textAlign: 'left' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Selected Files:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {Array.from(formData.attachments).map((file, index) => (
                          <Typography component="li" key={index} variant="body2">
                            {file.name} ({Math.round(file.size / 1024)} KB)
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        );
        
      case 3: // Review & Submit
        return (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Review Your Information
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Please review your service request details before submitting.
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Your service request will be reviewed by our team, and we'll get back to you within 24-48 business hours.
                </Alert>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Contact Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Full Name:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formData.fullName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Email:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formData.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Phone:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formData.phone || 'Not provided'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Company/Organization:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formData.company}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Divider />
                  
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Project Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Service Type:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {serviceOptions.find(opt => opt.value === formData.serviceType)?.label || 'Not selected'}
                          {formData.serviceType === 'other' && ` (${formData.otherService})`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Budget Range:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {budgetOptions.find(opt => opt.value === formData.budget)?.label || 'Not selected'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Expected Timeline:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {timelineOptions.find(opt => opt.value === formData.timeline)?.label || 'Not selected'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Project Description:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formData.projectDescription}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Divider />
                  
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Additional Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Preferred Communication:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formData.communicationPreference.map(pref => 
                            communicationOptions.find(opt => opt.value === pref)?.label
                          ).join(', ')}
                        </Typography>
                      </Grid>
                      
                      {formData.additionalInfo && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2">Additional Information:</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formData.additionalInfo}
                          </Typography>
                        </Grid>
                      )}
                      
                      {formData.howDidYouHear && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2">How did you hear about us:</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formData.howDidYouHear}
                          </Typography>
                        </Grid>
                      )}
                      
                      {formData.attachments && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2">Attachments:</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Array.from(formData.attachments).map(file => file.name).join(', ')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Card>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleReset}
                    sx={{ mr: 2 }}
                  >
                    Reset Form
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </Box>
                
                {submitError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </Grid>
          </motion.div>
        );
        
      default:
        return null;
    }
  };
  
  // If form was successfully submitted, show success message
  if (submitted) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <CheckCircleIcon 
            sx={{ 
              fontSize: 80, 
              color: 'success.main',
              mb: 2 
            }} 
          />
          <Typography variant="h4" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Your service request has been submitted successfully.
          </Typography>
          <Typography variant="body1" paragraph>
            Our team will review your request and get back to you within 24-48 business hours. A confirmation email has been sent to {formData.email}.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReset}
            sx={{ mt: 2 }}
          >
            Submit Another Request
          </Button>
        </motion.div>
      </Box>
    );
  }
  
  return (
    <Box>
      <Card 
        sx={{ 
          p: { xs: 2, md: 4 },
          mb: 4,
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(145deg, rgba(26, 26, 32, 0.9), rgba(30, 30, 40, 0.9))'
            : 'linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(246, 248, 255, 0.9))',
        }}
      >
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Service Request Form
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tell us about your project and requirements, and we'll get back to you with a customized solution.
          </Typography>
        </Box>
        
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel 
          sx={{ mb: 4 }}
        >
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <form>
          <Box sx={{ mt: 2 }}>
            {renderStepContent(activeStep)}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                >
                  Next
                </Button>
              ) : null}
            </Box>
          </Box>
        </form>
      </Card>
      
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(106, 27, 255, 0.1)'
            : 'rgba(94, 0, 255, 0.05)',
          display: 'flex',
          alignItems: 'flex-start',
          mb: 4,
        }}
      >
        <InfoIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
        <Box>
          <Typography variant="h6" gutterBottom>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you have any questions or need assistance filling out this form, please contact our sales team at <b>sales@soranova.com</b> or call us at <b>(555) 123-4567</b> during business hours.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceRequestForm;